/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React, { useEffect, useState } from 'react';
// redux hooks
import {
  useNavigate,
  useOutletContext,
  useParams,
  Link,
  useLocation
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../../store/userSlice';
// store and actions
import {
  getListing,
  getInstance,
  getRelatedInstances,
  addInstance,
  updateInstance,
  deleteInstance,
  fetchPossiblePages
} from '../../../actions';
import { showMessage } from '../../../store/BASE/messageSlice';
// form libraries
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
// BASE components
import _ from '../../../../@lodash';
import BASELoading from '../../../../@BASE/core/BASELoading';
// material ui components
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Badge from '@mui/material/Badge';
// custom components
import BasicInfoTab from './tabs/BasicInfoTab';
import TemplateTab from './tabs/TemplateTab';
import ContentTab from './tabs/ContentTab';
import NotesTab from './tabs/NotesTab';
import DefaultData from './data/Defaults';
import TemplateSchemas from './schema/TemplateSchema';

function PageForm(props) {
  const {
    instance,
    handleInstanceAction,
    loading,
    setOnSave,
    setSaveDisabled,
    setError
  } = useOutletContext();
  const [section, setSection] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [possiblePages, setPossiblePages] = useState([]);
  const [template, setTemplate] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [inputDir, setInputDir] = useState('ltr');
  const [contentSchema, setContentSchema] = useState(yup.object().shape({}));
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeParams = useParams();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const section_id =
    routeParams.id === 'new' ? queryParams.get('p') : instance.section.id;
  // define the schema
  const schema = yup.object().shape({
    title: yup.string().required('Title is required'),
    label: yup.string().required('Label is required'),
    screen: yup.string().required('Screen ID is required'),
    back: yup.string().required('Back ID is required'),
    next: yup.string().required('Next ID is required'),
    template: yup.string().notOneOf(['none'], 'Select a template').required(),
    content: contentSchema
  });
  // set state for the form
  const methods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      label: '',
      classification: 'static',
      progress: {
        development: 0,
        production: 0
      },
      status: {
        development: 'not_started',
        production: 'not_started'
      },
      section: section_id,
      template: '',
      screen: '',
      back: '',
      next: '',
      content: {}
    }
  });
  // form methods
  const {
    reset,
    watch,
    control,
    onChange,
    formState,
    handleSubmit,
    setValue,
    getValues
  } = methods;
  // form state
  const { isValid, dirtyFields, errors } = formState;
  // get parent
  const handleGetSection = async () => {
    if (instance?.section?.id) {
      setSection(instance.section);
      setInputDir(instance.section?.lesson?.course?.language?.rtl ? 'rtl' : 'ltr');
      setValue('section', instance.section.id);
    } else {
      try {
        const response = await dispatch(
          getInstance('sections')(section_id)
        ).unwrap();
        setSection(response);
        setInputDir(response?.lesson?.course?.language?.rtl ? 'rtl' : 'ltr');
        setValue('section', section_id);
      } catch (err) {
        setError(err.message);
      }
    }
  };
  // get the template listing
  const handleGetTemplates = async () => {
    try {
      const response = await dispatch(
        getListing('templates_summary')()
      ).unwrap();
      setTemplates(response);
    } catch (err) {
      setError(err.message);
    }
  };
  // ensure section and templates are only fetched once
  useEffect(() => {
    handleGetSection();
    handleGetTemplates();
  }, [dispatch, routeParams]);
  // handle tab change
  const handleTabChange = (event, value) => {
    setTabValue(value);
  };
  // get the selected template
  function getSelectedTemplate(template_id) {
    return templates?.find((template) => template.id === template_id);
  }
  // once the template is found, load the template
  const handleTemplateLoad = async (template, pageContentValue) => {
    let foundPossiblePages = [];
    // Optional: Fetch possible pages here
    try {
      const sectionToUse =
        section && Object.keys(section).length > 0
          ? section
          : instance?.section;

      const templateToUse =
        template && Object.keys(template).length > 0
          ? template
          : instance?.template;

      if (sectionToUse && templateToUse) {
        foundPossiblePages = await dispatch(
          fetchPossiblePages({
            section: sectionToUse,
            template: templateToUse
          })
        ).unwrap();
      }

      // Do something with foundPossiblePages, like setting it in state
      console.warn('Possible pages:', foundPossiblePages);
    } catch (err) {
      setError(err.message);
      console.log(err);
    }

    // make the key for data and schema
    const templateKey = template?.label.replace(/[\s:-]+/g, '');
    // update the default values for content
    const contentValues = pageContentValue || DefaultData[templateKey] || {};
    // update the content value
    setValue('content', contentValues);
    // update the validation schema
    const contentSchema =
      TemplateSchemas[templateKey] || yup.object().shape({});
    setContentSchema(contentSchema);
    // update the selected template
    setTemplate(template);
    setPossiblePages(foundPossiblePages);
  };
  // handle template change
  const handleTemplateChange = async (value, pageContentValue) => {
    // get the template
    const templateId = value;
    const template = getSelectedTemplate(templateId);

    await handleTemplateLoad(template, pageContentValue);
    // set the template value
  };

  // set the template value if it exists
  useEffect(() => {
    const loadTemplate = async () => {
      if (!instance) return;

      if (instance.template && instance.template.id !== '') {
        await handleTemplateLoad(instance.template, instance.content);
        setValue('template', instance.template?.id);
      }
    };

    loadTemplate();
  }, [instance]);

  // handle form submission
  function onSubmit(data) {
    // update the form data to include the user id
    data.user = user._id;

    if (routeParams.id === 'new') {
      handleInstanceAction(addInstance('pages')(data), 'post').then(() => {
        navigate(`../sections/${section_id}`);
      });
    } else {
      handleInstanceAction(updateInstance('pages')(data), 'patch');
    }
  }
  // handle form errors
  const basicErrors =
    formState.errors.title ||
    formState.errors.label ||
    formState.errors.screen ||
    formState.errors.back ||
    formState.errors.next;
  const contentErrors = formState.errors.template || formState.errors.content;
  const onError = (errors, e) => {
    dispatch(
      showMessage({
        message: 'Saved Failed! Check form inputs.',
        variant: 'error'
      })
    );
  };
  // Pass the handleSubmit function to setOnSave
  useEffect(() => {
    setOnSave(() => handleSubmit(onSubmit, onError));
  }, []);
  // Update the parent component with the form's validity and dirty state
  useEffect(() => {
    // setSaveDisabled(_.isEmpty(dirtyFields) || !isValid);
    setSaveDisabled(false);
  }, [formState]);
  // on instance change, reset the form
  useEffect(() => {
    let data = {
      ...instance,
      section: section_id,
      template: instance.template?.id || '',
      user: user._id
    };
    reset({ ...data });
  }, [instance, reset]);
  // if the page instance is loading, show a loading spinner
  if (
    !instance ||
    (instance && routeParams.id !== instance.id && routeParams.id !== 'new')
  ) {
    return <BASELoading />;
  }
  if (!section) {
    return <BASELoading />;
  }
  // if the templates are loading, show a loading spinner
  if (!templates) {
    return <BASELoading />;
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col flex-auto w-full mb-64 px-24 sm:px-24">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="secondary"
          textColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
          classes={{ root: 'w-full h-64 border-b-1' }}
        >
          <Tab
            className="h-64"
            label={
              <>
                {basicErrors ? (
                  <Badge color="error" variant="dot">
                    Basic Info
                  </Badge>
                ) : (
                  'Basic Info'
                )}
              </>
            }
          />
          <Tab
            className="h-64"
            label={
              <>
                {formState.errors.template_id ? (
                  <Badge color="error" variant="dot">
                    Template
                  </Badge>
                ) : (
                  'Template'
                )}
              </>
            }
          />
          <Tab
            className="h-64"
            label={
              <>
                {contentErrors ? (
                  <Badge color="error" variant="dot">
                    Content
                  </Badge>
                ) : (
                  'Content'
                )}
              </>
            }
          />
          <Tab className="h-64" label="Development Notes" />
        </Tabs>
        <div className="p-16 sm:p-24 max-w-4xl">
          <div className={tabValue !== 0 ? 'hidden' : ''}>
            <BasicInfoTab instance={instance} section={section} />
          </div>
          <div className={tabValue !== 1 ? 'hidden' : ''}>
            <TemplateTab
              templates={templates}
              template={template}
              handleTemplateChange={handleTemplateChange}
            />
          </div>
          <div className={tabValue !== 2 ? 'hidden' : ''}>
            <ContentTab
              template={template}
              inputDir={inputDir}
              section={section}
              possiblePages={possiblePages}
            />
          </div>
          <div className={tabValue !== 3 ? 'hidden' : ''}>
            <NotesTab template={template} inputDir={inputDir} />
          </div>
        </div>
      </div>
    </FormProvider>
  );
}

export default PageForm;
